// @ts-strict-ignore
import { DrillingholePresetVO } from '@project/actions/drilling-hole/drilling-hole.vo';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { DrillingHoleInfo } from '@3d-scene/model3d/util/drillinghole/drilling-hole-info';
import { RangeOnLineConnectionUtils } from '@3d-scene/model3d/util/range-on-line-connection-utils';
import { ContainerService } from '../../../container.service';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { RangeOnLineConnectionTypeDef } from '@project/constants/range-on-line-connection-type-def';
import { Line } from '@3d-scene/model/line';
import { Connection } from '@3d-scene/model3d/connection';
import { DrillingHoleUtils } from '@3d-scene/model3d/util/drillinghole/drilling-hole-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { CreateDrillingHolePresenter } from '@3d-scene/model3d/util/drillinghole/presenter/create-drilling-hole.presenter';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { SelectionModel } from '@project/components/article/model/selection.model';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { DrillingHoleService } from '@project/actions/drilling-hole/drilling-hole.service';
import { Injectable } from '@angular/core';
import { InsertArticleUtils } from '@project/components/article/util/insert-article-utils';
import { ConnectionInfoUtils } from '../../connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { MiniTerminalBlocksProjectCreationServiceDelegator } from '@configuration/wizard/service/mini-terminal-blocks-project-creation-service.delegator';
import { ArticleUsageService } from '@article/service/article-usage.service';

@Injectable({ providedIn: 'root' })
export class DrillingHolePresetPresenter {
  private _mountingRailLength: number;
  private _drillingholeOffset: number;
  private _drillingholePresetVO: DrillingholePresetVO;
  private _mountingRailArticleUsage: ArticleUsageVO;
  private _defaultDrillingHoles: DrillingHoleInfo[];

  constructor(
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private detachArticleUsageService: DetachArticleUsageService,
    private drillingHolePresenter: CreateDrillingHolePresenter,
    protected selectionModel: SelectionModel,
    private overlayService: OverlayService,
    private sceneManager: SceneManager,
    private miniTerminalBlocksProjectCreationServiceDelegator: MiniTerminalBlocksProjectCreationServiceDelegator,
    private insertArticleUtils: InsertArticleUtils,
    private containerService: ContainerService,
    private drillingHoleService: DrillingHoleService,
    private articleUsageService: ArticleUsageService
  ) {}

  public clearDrillingHolePresetProperties(mountingRailArticleUsage: ArticleUsageVO): PropertyVO[] {
    if (!mountingRailArticleUsage) {
      return [];
    }
    return this.addPresetUsageInfoPropertiesToMountingRailArticleUsage(mountingRailArticleUsage, false);
  }

  public getDrillingHolePresetProperties(mountingRailArticleUsage: ArticleUsageVO, cloneProperties: boolean = true): PropertyVO[] {
    if (!mountingRailArticleUsage) {
      return [];
    }
    const originalProperties: PropertyVO[] = [];
    let articleUsageInfoProperty: PropertyVO = ArticleUsageUtils.getArticleUsageInfoProperty(
      mountingRailArticleUsage,
      ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET
    );
    if (articleUsageInfoProperty) {
      if (cloneProperties) {
        originalProperties.push(articleUsageInfoProperty.clone());
      } else {
        originalProperties.push(articleUsageInfoProperty);
      }
    }
    articleUsageInfoProperty = ArticleUsageUtils.getArticleUsageInfoProperty(
      mountingRailArticleUsage,
      ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_DISTANCE
    );
    if (articleUsageInfoProperty) {
      if (cloneProperties) {
        originalProperties.push(articleUsageInfoProperty.clone());
      } else {
        originalProperties.push(articleUsageInfoProperty);
      }
    }
    articleUsageInfoProperty = ArticleUsageUtils.getArticleUsageInfoProperty(
      mountingRailArticleUsage,
      ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_OFFSET
    );
    if (articleUsageInfoProperty) {
      if (cloneProperties) {
        originalProperties.push(articleUsageInfoProperty.clone());
      } else {
        originalProperties.push(articleUsageInfoProperty);
      }
    }
    return originalProperties;
  }

  public async createDrillingHolePreset(mountingRailLength: number, drillingHoleOffset: number, drillingHolePreset: DrillingholePresetVO,
    mountingRailArticleUsage: ArticleUsageVO, pushUndoOperations: boolean = false): Promise<boolean> {
    if (!drillingHolePreset) {
      await this.handlePresetLoadingError();
      return false;
    }
    this._mountingRailLength = mountingRailLength;
    this._drillingholeOffset = drillingHoleOffset;
    this._drillingholePresetVO = drillingHolePreset;
    this._mountingRailArticleUsage = mountingRailArticleUsage;

    const maxDrillingholePositionOffsetOnRail: number = DrillingHoleUtils.getMaxDrillingHolePositionOffsetOnRail(
      drillingHolePreset.drillingholeArticle
    );

    const freeSpace: number = this._mountingRailLength + maxDrillingholePositionOffsetOnRail - drillingHoleOffset;
    drillingHoleOffset = UnitConverter.fromTo('mm', '3d', drillingHoleOffset);
    const drillingholeCount: number = Math.floor(freeSpace / drillingHolePreset.distance);
    const distance: number = UnitConverter.fromTo('mm', '3d', drillingHolePreset.distance);

    const result: DrillingHoleInfo[] = [];
    const drillingholeArticles: ArticleVO[] = [];
    let info: DrillingHoleInfo = null;
    let i: number = 0;
    do {
      info = new DrillingHoleInfo();
      info.position = DrillingHoleUtils.roundDrillingHolePosition(drillingHoleOffset + distance * i);
      info.positionOffset = RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();
      info.shapeType = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(drillingHolePreset.drillingholeArticle);
      info.size = DrillingHoleUtils.getDrillingHoleSizeByArticle(drillingHolePreset.drillingholeArticle);
      result.push(info);
      if (i === 0) {
        drillingholeArticles.push(drillingHolePreset.drillingholeArticle);
      }
      i++;
    } while (i <= drillingholeCount);

    const createNewHoles: DrillingHoleInfo[] = [];
    for (const drillingHole of result) {
      createNewHoles.push(drillingHole);
    }
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(this._mountingRailArticleUsage.id);
    const acceptingConnectionArea: ConnectionAreaVO = ConnectionInfoUtils.findRangeOnLineConnectionByType(
      articleContainer.article.metaElements,
      RangeOnLineConnectionTypeDef.TYPE_DRILLING_HOLE
    );
    let baseline: Line = null;
    if (acceptingConnectionArea) {
      const acceptingConnection: Connection = articleContainer.findConnectionByName(acceptingConnectionArea.name);
      if (!acceptingConnection) {
        await this.handlePresetLoadingError();
        return false;
      }
      baseline = new Line(acceptingConnection.localRectangle.point1, acceptingConnection.localRectangle.point4);
    } else {
      await this.handlePresetLoadingError();
      return false;
    }
    this.refreshDrillingHoleList(this._mountingRailArticleUsage);
    const removeDefaultHoles: DrillingHoleInfo[] = this._defaultDrillingHoles.concat();
    await this.drillingHolePresenter.createDrillingHoles(createNewHoles, this._mountingRailArticleUsage, drillingholeArticles,
      this.getDrillingHoleGUIDs(removeDefaultHoles), baseline, true, pushUndoOperations);
    this.addPresetUsageInfoPropertiesToMountingRailArticleUsage(this._mountingRailArticleUsage, true, this._drillingholeOffset,
      this._drillingholePresetVO.distance);
    return true;
  }

  private async fillInMissingDrillingHolesForPreset(mountingRailLength: number, deltaMountingRailLength: number, lastDrillingholePosition: number,
      drillingholePresetVO: DrillingholePresetVO, mountingRailArticleUsage: ArticleUsageVO, pushUndoOperations: boolean): Promise<boolean> {
    this._mountingRailArticleUsage = mountingRailArticleUsage;
    this._mountingRailLength = mountingRailLength;
    this._drillingholePresetVO = drillingholePresetVO;

    const freeSpace: number = this._mountingRailLength - lastDrillingholePosition;
    const distance: number = UnitConverter.fromTo('mm', '3d', drillingholePresetVO.distance);
    const drillingholeCount: number = Math.floor(freeSpace / distance);
    const result: DrillingHoleInfo[] = [];
    const drillingholeArticles: ArticleVO[] = [];
    let info: DrillingHoleInfo;
    let i: number = 1;
    if (drillingholeCount <= 0) {
      this.drillingHoleService.notifyDrillingHolesInserted();
      await this.miniTerminalBlocksProjectCreationServiceDelegator.mountingRailAttached();
      this.drillingHoleService.notifyPresetCompleted(true);
      return false;
    }
    do {
      info = new DrillingHoleInfo();
      info.position = DrillingHoleUtils.roundDrillingHolePosition(lastDrillingholePosition + distance * i);
      info.positionOffset = RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();
      info.shapeType = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(drillingholePresetVO.drillingholeArticle);
      info.size = DrillingHoleUtils.getDrillingHoleSizeByArticle(drillingholePresetVO.drillingholeArticle);
      result.push(info);
      if (i === 1) {
        drillingholeArticles.push(drillingholePresetVO.drillingholeArticle);
      }
      i++;
    } while (i <= drillingholeCount);

    const createNewHoles: DrillingHoleInfo[] = [];
    for (const drillingHole of result) {
      createNewHoles.push(drillingHole);
    }
    const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(this._mountingRailArticleUsage.id);
    const acceptingConnectionArea: ConnectionAreaVO = ConnectionInfoUtils.findRangeOnLineConnectionByType(
      articleContainer.article.metaElements,
      RangeOnLineConnectionTypeDef.TYPE_DRILLING_HOLE
    );
    let baseline: Line;
    if (acceptingConnectionArea) {
      const acceptingConnection: Connection = articleContainer.findConnectionByName(acceptingConnectionArea.name);
      if (!acceptingConnection) {
        await this.handlePresetLoadingError();
        return false;
      }
      baseline = new Line(acceptingConnection.localRectangle.point1, acceptingConnection.localRectangle.point4);
    } else {
      await this.handlePresetLoadingError();
      return false;
    }
    this.refreshDrillingHoleList(this._mountingRailArticleUsage);
    await this.drillingHolePresenter.createDrillingHoles(createNewHoles, this._mountingRailArticleUsage, drillingholeArticles, [], baseline, false, pushUndoOperations);
    return true;
  }

  public async updateDrillingHoles(mountingRailArticleUsage: ArticleUsageVO, mountingRailLength: number, newMountingRailLength: number, deltaMountingRailLength: number,
    newDrillingHoleOffset: number, drillingHoleOffsetDelta: number, drillingHolePreset: DrillingholePresetVO, pushUndoOperations: boolean): Promise<void> {
    await this.internalUpdateDrillingHoles(mountingRailArticleUsage, mountingRailLength, newMountingRailLength, deltaMountingRailLength,
      drillingHoleOffsetDelta, drillingHolePreset, pushUndoOperations);
    this.addPresetUsageInfoPropertiesToMountingRailArticleUsage(mountingRailArticleUsage, drillingHolePreset ? true : false, newDrillingHoleOffset,
      drillingHolePreset ? drillingHolePreset.distance : NaN);
  }

  private async handlePresetLoadingError(): Promise<void> {
    this.overlayService.requestOverlayHide(OverlayBackground.color);
    this.alertHelper.showError(
      'newproject.mountingRail.alert.title.loadPresetFailed',
      'newproject.mountingRail.alert.message.loadPresetFailed');
    await this.miniTerminalBlocksProjectCreationServiceDelegator.mountingRailAttached();
    this.drillingHoleService.notifyPresetCompleted(false);
  }

  private addPresetUsageInfoPropertiesToMountingRailArticleUsage(
    mountingRailArticleUsage: ArticleUsageVO,
    hasPreset: boolean,
    drillingholeOffset: number = NaN,
    drillingholeDistance: number = NaN
  ): PropertyVO[] {
    const originalProperties: PropertyVO[] = this.getDrillingHolePresetProperties(mountingRailArticleUsage, true);
    ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(
      mountingRailArticleUsage,
      ArticleUsageInfoPropertyDef.HAS_DRILLINGHOLE_PRESET,
      hasPreset
    );
    if (hasPreset) {
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
        mountingRailArticleUsage,
        ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_DISTANCE,
        drillingholeDistance
      );
      ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
        mountingRailArticleUsage,
        ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_OFFSET,
        drillingholeOffset
      );
    } else {
      ArticleUsageUtils.removeArticleUsageInfoProperty(mountingRailArticleUsage, ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_DISTANCE);
      ArticleUsageUtils.removeArticleUsageInfoProperty(mountingRailArticleUsage, ArticleUsageInfoPropertyDef.DRILLINGHOLE_PRESET_OFFSET);
    }
    return originalProperties;
  }

  private async internalUpdateDrillingHoles(articleUsage: ArticleUsageVO, mountingRailLength: number, newMountingRailLength: number,
      deltaMountingRailLength: number, drillingholeOffsetDelta: number, drillingholePreset: DrillingholePresetVO,
      pushUndoOperations: boolean): Promise<void> {
    this.sceneManager.renderScene();
    let childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    const childGuidsToDetach: string[] = [];
    const childUsages: ArticleUsageVO[] = [];
    const moveMap: Dictionary<string, Vector3> = new Dictionary<string, Vector3>();
    const deltaOffset: number = UnitConverter.fromTo('mm', '3d', drillingholeOffsetDelta);
    const negativeDelta: boolean = deltaOffset < 0 ? true : false;
    let length: number;
    const maxDrillingHolePositionOffsetOnRail: number = DrillingHoleUtils.getMaxDrillingHolePositionOffsetOnRail(
      drillingholePreset.drillingholeArticle,
      true
    );
    let maxPosition: number = -maxDrillingHolePositionOffsetOnRail;
    if (mountingRailLength <= newMountingRailLength) {
      length = deltaMountingRailLength < 0 ? mountingRailLength : newMountingRailLength;
    } else {
      length = deltaMountingRailLength < 0 ? newMountingRailLength : mountingRailLength;
    }
    length += maxDrillingHolePositionOffsetOnRail;
    for (const guid of childGUIDs) {
      const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      if (this.articleUsageService.isDrillingHoleArticleUsage(childUsage)) {
        const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(guid);
        moveMap.setValue(articleContainer.articleGUID, new Vector3(negativeDelta ? deltaOffset : -deltaOffset, 0, 0));
        const position: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(childUsage, ArticleUsageInfoPropertyDef.ARTICLE_OFFSET);

        if (position + deltaOffset > length) {
          childGuidsToDetach.push(childUsage.id);
        }

        if (position + deltaOffset >= maxPosition && position + deltaOffset <= length) {
          maxPosition = position + deltaOffset;
          childUsages.push(childUsage);
        }

        if (position + deltaOffset <= -maxDrillingHolePositionOffsetOnRail && position + deltaOffset <= length) {
          maxPosition = position + deltaOffset;
          childUsages.push(childUsage);
        }
      }
    }
    this.insertArticleUtils.moveArticlesByMap(moveMap, negativeDelta);
    await this.detachArticleUsageService.detachArticleUsageFromProjectByIds(childGuidsToDetach);
    childGUIDs = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    this.articleUsageService.updateDrillingHoleGeometricData(childUsages);
    this.sceneManager.renderScene();
    await this.fillInMissingDrillingHolesForPreset(length, deltaMountingRailLength, maxPosition, drillingholePreset, articleUsage,
      pushUndoOperations);
  }

  private refreshDrillingHoleList(articleUsage: ArticleUsageVO): void {
    const drillingHoleObjects: DrillingHoleInfo[] = [];
    const childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
    for (const guid of childGUIDs) {
      const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
      if (this.articleUsageService.isDrillingHoleArticleUsage(childUsage)) {
        const childArticle: ArticleVO = this.articleUsageService.getArticleByArticleUsage(childUsage);
        if (childArticle) {
          const drillingHoleInfo: DrillingHoleInfo = new DrillingHoleInfo();
          drillingHoleInfo.usageId = childUsage.id;
          drillingHoleInfo.positionOffset = RangeOnLineConnectionUtils.getHalfStretchLengthInAwayUnits();
          drillingHoleInfo.position = -ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(
            childUsage,
            ArticleUsageInfoPropertyDef.ARTICLE_OFFSET
          );
          drillingHoleInfo.shapeType = DrillingHoleUtils.getDrillingHoleShapeTypeByArticle(childArticle);
          drillingHoleInfo.size = DrillingHoleUtils.getDrillingHoleSizeByArticle(childArticle);
          drillingHoleObjects.push(drillingHoleInfo);
        }
      }
    }
    DrillingHoleUtils.sortDrillingHolesByPosition(drillingHoleObjects);
    this._defaultDrillingHoles = DrillingHoleUtils.copyDrillingHoleArray(drillingHoleObjects);
  }

  public getDrillingHoleGUIDs(drillingHoles: DrillingHoleInfo[]): string[] {
    const result: string[] = [];
    for (const holeInfo of drillingHoles) {
      if (holeInfo.usageId) {
        result.push(holeInfo.usageId);
      }
    }
    return result;
  }
}
